'use client';

import { useState, Suspense, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { ArrowRight, CheckCircle2, Mail, RotateCcw, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/providers/AuthProvider';
import { useCart } from '@/providers/CartProvider';
import { useWishlist } from '@/providers/WishlistProvider';
import { useBasePath } from '@/providers/BasePathProvider';
import { useTheme } from '@/providers/ThemeProvider';
import { AuthApi } from '@/lib/Apis/apis/auth-api';
import { getApiConfiguration } from '@/lib/api-adapter/config';
import { formatPhoneNumber } from '@/lib/utils/format';

// ─── OTP helpers ─────────────────────────────────────────────────────────────

function OtpCodeInput({
  value,
  onChange,
  accentColor,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  accentColor?: string;
}) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

  const handleChange = (idx: number, raw: string) => {
    if (raw.length > 1) {
      const digits = raw.replace(/\D/g, '').slice(0, 6).split('');
      const next = [...value];
      for (let i = 0; i < 6; i++) next[i] = digits[i] ?? '';
      onChange(next);
      inputRefs.current[Math.min(digits.length, 5)]?.focus();
      return;
    }
    const digit = raw.replace(/\D/g, '');
    const next = [...value];
    next[idx] = digit;
    onChange(next);
    if (digit && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          className="w-11 h-12 sm:w-12 sm:h-14 text-center text-xl font-bold border-2 border-gray-200 rounded-xl focus:outline-none transition-colors"
          style={value[i] ? accentColor ? { borderColor: accentColor, color: accentColor } : undefined : undefined}
        />
      ))}
    </div>
  );
}

function useOtpCountdown() {
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const start = (seconds = 60) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setCountdown(seconds);
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(intervalRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);
  return { countdown, start };
}

// ─── Slide variants ───────────────────────────────────────────────────────────

const slideVariants = {
  enter: (dir: number) => ({ x: dir * 48, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: -dir * 48, opacity: 0 }),
};
const slideTransition = { duration: 0.28, ease: [0.25, 0.1, 0.25, 1] };

// ─── Accent style helpers ─────────────────────────────────────────────────────

function accentBg(color?: string): React.CSSProperties | undefined {
  return color ? { backgroundColor: color } : undefined;
}
function accentText(color?: string): React.CSSProperties | undefined {
  return color ? { color } : undefined;
}

// ─── Left panel ───────────────────────────────────────────────────────────────

function LeftPanel({ accentColor, panelImage, badge, features = [], quote }: {
  accentColor?: string; panelImage?: string; badge?: string; features?: string[]; quote?: string;
}) {
  const { config } = useTheme();

  return (
    <div
      className="hidden lg:flex lg:w-5/12 relative flex-col items-start gap-y-12 p-12 overflow-hidden rounded-l-2xl bg-hprimary"
      style={accentBg(accentColor)}
    >
      {panelImage && (
        <>
          <Image src={panelImage} alt="" fill className="object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
        </>
      )}

      {badge && (
        <div className="relative z-10 flex items-center gap-3 text-white/70 text-xs font-semibold tracking-[0.25em] uppercase">
          <span className="w-8 h-px bg-white/50" />
          {badge}
        </div>
      )}

      <div className="relative z-10 space-y-8">
        <Image
          src={config.logo}
          alt={config.storeName}
          width={220}
          height={70}
          className="brightness-0 invert opacity-90"
        />
        {quote && (
          <blockquote className="text-white/90 text-2xl italic leading-relaxed text-left">
            &quot;{quote}&quot;
          </blockquote>
        )}
        {features.length > 0 && (
          <ul className="space-y-3">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-3 text-white/80 text-sm">
                <CheckCircle2 className="h-4 w-4 text-white/60 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="relative z-10" />
    </div>
  );
}

// ─── Login form ───────────────────────────────────────────────────────────────

type LoginOtpStep = 'email' | 'code';

function LoginForm({
  onSwitchTab,
  redirectUrl,
  accentColor,
}: {
  onSwitchTab: () => void;
  redirectUrl: string;
  accentColor?: string;
}) {
  const router = useRouter();
  const { loginWithSession } = useAuth();
  const { mergeGuestCart } = useCart();
  const { mergeGuestWishlist } = useWishlist();
  const [otpStep, setOtpStep] = useState<LoginOtpStep>('email');
  const [direction, setDirection] = useState(1);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState<string[]>(Array(6).fill(''));
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [error, setError] = useState<{ message: string; showRegisterLink?: boolean } | null>(null);
  const { countdown, start: startCountdown } = useOtpCountdown();

  const handleSendOtp = async () => {
    if (!otpEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(otpEmail)) {
      setError({ message: 'Enter a valid email address.' });
      return;
    }
    setIsSendingOtp(true);
    setError(null);
    try {
      await new AuthApi(getApiConfiguration()).sendOtp({ email: otpEmail });
      startCountdown(60);
      setDirection(1);
      setOtpStep('code');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to send code. Try again.';
      setError({ message: msg, showRegisterLink: err?.response?.status === 400 });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    const code = otpCode.join('');
    if (code.length < 6) { setError({ message: 'Enter all 6 digits of your code.' }); return; }
    setIsVerifyingOtp(true);
    setError(null);
    try {
      const res = await new AuthApi(getApiConfiguration()).verifyOtp({ email: otpEmail, code });
      const data = (res as any).data;
      await loginWithSession(data.authToken, data.userData);
      await mergeGuestCart();
      await mergeGuestWishlist();
      router.push(redirectUrl);
    } catch (err: any) {
      setError({ message: err?.response?.data?.message || 'Invalid or expired code.' });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    setError(null);
    setOtpCode(Array(6).fill(''));
    setIsSendingOtp(true);
    try {
      await new AuthApi(getApiConfiguration()).sendOtp({ email: otpEmail });
      startCountdown(60);
    } catch (err: any) {
      setError({ message: err?.response?.data?.message || 'Failed to resend code.' });
    } finally {
      setIsSendingOtp(false);
    }
  };

  return (
    <>
      {error && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0 text-red-500 mt-0.5" />
          <span>
            {error.message}
            {error.showRegisterLink && (
              <> <button onClick={onSwitchTab} className="underline font-medium">Register here</button>.</>
            )}
          </span>
        </div>
      )}

      <AnimatePresence initial={false} custom={direction} mode="wait">
        {otpStep === 'email' ? (
          <motion.div
            key="login-email"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
            className="space-y-6"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address <span className="text-red-400">*</span>
              </label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={otpEmail}
                onChange={(e) => { setOtpEmail(e.target.value); setError(null); }}
                leftElement={<Mail className="h-4 w-4 text-gray-400" />}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSendOtp(); } }}
              />
            </div>
            <button
              type="button"
              disabled={isSendingOtp}
              onClick={handleSendOtp}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-lg bg-hprimary text-white text-sm font-semibold transition-opacity disabled:opacity-60"
              style={accentBg(accentColor)}
            >
              {isSendingOtp ? 'Sending…' : <>Send Code <ArrowRight className="h-4 w-4" /></>}
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="login-code"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
            className="space-y-6"
          >
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">
                We sent a 6-digit code to <strong>{otpEmail}</strong>
              </p>
              <p className="text-xs text-gray-400">The code expires in 10 minutes.</p>
            </div>
            <OtpCodeInput value={otpCode} onChange={setOtpCode} accentColor={accentColor} />
            <button
              type="button"
              disabled={isVerifyingOtp}
              onClick={handleVerifyOtp}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-lg bg-hprimary text-white text-sm font-semibold transition-opacity disabled:opacity-60"
              style={accentBg(accentColor)}
            >
              {isVerifyingOtp ? 'Verifying…' : <>Verify Code <ArrowRight className="h-4 w-4" /></>}
            </button>
            <div className="flex items-center justify-between text-sm text-gray-500">
              <button
                type="button"
                onClick={() => { setDirection(-1); setOtpStep('email'); setError(null); }}
                className="hover:text-gray-700 transition-colors"
              >
                ← Change email
              </button>
              {countdown > 0 ? (
                <span className="text-gray-400">Resend in {countdown}s</span>
              ) : (
                <button
                  type="button"
                  disabled={isSendingOtp}
                  onClick={handleResendOtp}
                  className="flex items-center gap-1.5 text-hprimary hover:opacity-70 font-medium disabled:opacity-50"
                  style={accentText(accentColor)}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {isSendingOtp ? 'Sending…' : 'Resend code'}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-8 pt-6 border-t border-gray-100 text-center">
        <p className="text-sm text-gray-500">
          Don&apos;t have an account?{' '}
          <button
            onClick={onSwitchTab}
            className="font-medium text-hprimary hover:opacity-70 transition-opacity"
            style={accentText(accentColor)}
          >
            Create one
          </button>
        </p>
      </div>
    </>
  );
}

// ─── Register form ────────────────────────────────────────────────────────────

type RegOtpStep = 'details' | 'code';

function RegisterForm({
  onSwitchTab,
  accentColor,
}: {
  onSwitchTab: () => void;
  accentColor?: string;
}) {
  const router = useRouter();
  const { buildPath } = useBasePath();
  const { loginWithSession } = useAuth();
  const { mergeGuestCart } = useCart();
  const { mergeGuestWishlist } = useWishlist();

  const [regOtpStep, setRegOtpStep] = useState<RegOtpStep>('details');
  const [direction, setDirection] = useState(1);
  const [regOtpEmail, setRegOtpEmail] = useState('');
  const [regOtpFirstname, setRegOtpFirstname] = useState('');
  const [regOtpLastname, setRegOtpLastname] = useState('');
  const [regOtpPhone, setRegOtpPhone] = useState('');
  const [regOtpDob, setRegOtpDob] = useState('');
  const [regOtpCode, setRegOtpCode] = useState<string[]>(Array(6).fill(''));
  const [isSendingRegOtp, setIsSendingRegOtp] = useState(false);
  const [isVerifyingRegOtp, setIsVerifyingRegOtp] = useState(false);
  const [error, setError] = useState<{ message: string; showLoginLink?: boolean } | null>(null);
  const { countdown: regCountdown, start: startRegCountdown } = useOtpCountdown();

  const maxDobDate = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().split('T')[0]; })();
  const minDobDate = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 120); return d.toISOString().split('T')[0]; })();

  const getCleanPhone = (phone: string) => {
    const digits = phone.replace(/\D/g, '');
    // digits includes the leading 1 from +1, so need 11 total for a US number
    if (digits.length < 11) return undefined;
    return phone.replace(/[\s-]/g, '').trim();
  };

  const handleSendRegOtp = async () => {
    if (!regOtpEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regOtpEmail)) {
      setError({ message: 'Enter a valid email address.' });
      return;
    }
    if (!regOtpFirstname.trim() || !regOtpLastname.trim()) {
      setError({ message: 'Enter your first and last name.' });
      return;
    }
    const phoneDigits = regOtpPhone.replace(/\D/g, '');
    if (phoneDigits.length > 0 && phoneDigits.length < 11) {
      setError({ message: 'Enter a valid 10-digit phone number.' });
      return;
    }
    setIsSendingRegOtp(true);
    setError(null);
    try {
      await new AuthApi(getApiConfiguration()).sendRegisterOtp({
        email: regOtpEmail,
        firstname: regOtpFirstname,
        lastname: regOtpLastname,
        phoneNumber: getCleanPhone(regOtpPhone),
        dateOfBirth: regOtpDob ? new Date(regOtpDob) : undefined,
      });
      startRegCountdown(60);
      setDirection(1);
      setRegOtpStep('code');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to send code. Try again.';
      setError({ message: msg, showLoginLink: err?.response?.status === 400 });
    } finally {
      setIsSendingRegOtp(false);
    }
  };

  const handleVerifyRegOtp = async () => {
    const code = regOtpCode.join('');
    if (code.length < 6) { setError({ message: 'Enter all 6 digits of your code.' }); return; }
    setIsVerifyingRegOtp(true);
    setError(null);
    try {
      const res = await new AuthApi(getApiConfiguration()).verifyOtp({ email: regOtpEmail, code });
      const data = (res as any).data;
      await loginWithSession(data.authToken, data.userData);
      await mergeGuestCart();
      await mergeGuestWishlist();
      router.push(buildPath('/'));
    } catch (err: any) {
      setError({ message: err?.response?.data?.message || 'Invalid or expired code.' });
    } finally {
      setIsVerifyingRegOtp(false);
    }
  };

  const handleResendRegOtp = async () => {
    if (regCountdown > 0) return;
    setError(null);
    setRegOtpCode(Array(6).fill(''));
    setIsSendingRegOtp(true);
    try {
      await new AuthApi(getApiConfiguration()).sendRegisterOtp({
        email: regOtpEmail,
        firstname: regOtpFirstname,
        lastname: regOtpLastname,
        phoneNumber: getCleanPhone(regOtpPhone),
        dateOfBirth: regOtpDob ? new Date(regOtpDob) : undefined,
      });
      startRegCountdown(60);
    } catch (err: any) {
      setError({ message: err?.response?.data?.message || 'Failed to resend code.' });
    } finally {
      setIsSendingRegOtp(false);
    }
  };

  return (
    <>
      {error && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0 text-red-500 mt-0.5" />
          <span>
            {error.message}
            {error.showLoginLink && (
              <> <button onClick={onSwitchTab} className="underline font-medium">Log in here</button>.</>
            )}
          </span>
        </div>
      )}

      <AnimatePresence initial={false} custom={direction} mode="wait">
        {regOtpStep === 'details' ? (
          <motion.div
            key="reg-details"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
            className="space-y-5"
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">First name <span className="text-red-400">*</span></label>
                <Input placeholder="Alex" value={regOtpFirstname} onChange={(e) => { setRegOtpFirstname(e.target.value); setError(null); }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Last name <span className="text-red-400">*</span></label>
                <Input placeholder="Morgan" value={regOtpLastname} onChange={(e) => { setRegOtpLastname(e.target.value); setError(null); }} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address <span className="text-red-400">*</span></label>
              <Input
                type="email"
                placeholder="you@example.com"
                value={regOtpEmail}
                onChange={(e) => { setRegOtpEmail(e.target.value); setError(null); }}
                leftElement={<Mail className="h-4 w-4 text-gray-400" />}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number <span className="text-gray-400 font-normal">(optional)</span></label>
              <Input
                type="tel"
                placeholder="+1 555 000-0000"
                value={regOtpPhone}
                onChange={(e) => setRegOtpPhone(formatPhoneNumber(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Date of Birth <span className="text-gray-400 font-normal">(optional)</span></label>
              <Input
                type="date"
                value={regOtpDob}
                max={maxDobDate}
                min={minDobDate}
                onChange={(e) => setRegOtpDob(e.target.value)}
              />
            </div>
            <button
              type="button"
              disabled={isSendingRegOtp}
              onClick={handleSendRegOtp}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-hprimary text-white text-sm font-semibold transition-opacity disabled:opacity-60"
              style={accentBg(accentColor)}
            >
              {isSendingRegOtp ? 'Sending code…' : <>Create Account <ArrowRight className="h-4 w-4" /></>}
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="reg-code"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
            className="space-y-6"
          >
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">
                We sent a 6-digit code to <strong>{regOtpEmail}</strong>
              </p>
              <p className="text-xs text-gray-400">The code expires in 10 minutes.</p>
            </div>
            <OtpCodeInput value={regOtpCode} onChange={setRegOtpCode} accentColor={accentColor} />
            <button
              type="button"
              disabled={isVerifyingRegOtp}
              onClick={handleVerifyRegOtp}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-hprimary text-white text-sm font-semibold transition-opacity disabled:opacity-60"
              style={accentBg(accentColor)}
            >
              {isVerifyingRegOtp ? 'Verifying…' : <>Verify Code <ArrowRight className="h-4 w-4" /></>}
            </button>
            <div className="flex items-center justify-between text-sm text-gray-500">
              <button
                type="button"
                onClick={() => { setDirection(-1); setRegOtpStep('details'); setError(null); }}
                className="hover:text-gray-700 transition-colors"
              >
                ← Edit details
              </button>
              {regCountdown > 0 ? (
                <span className="text-gray-400">Resend in {regCountdown}s</span>
              ) : (
                <button
                  type="button"
                  disabled={isSendingRegOtp}
                  onClick={handleResendRegOtp}
                  className="flex items-center gap-1.5 text-hprimary hover:opacity-70 font-medium disabled:opacity-50"
                  style={accentText(accentColor)}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {isSendingRegOtp ? 'Sending…' : 'Resend code'}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="text-center text-sm text-gray-500 mt-6">
        Already have an account?{' '}
        <button
          onClick={onSwitchTab}
          className="font-medium text-hprimary hover:opacity-70 transition-opacity"
          style={accentText(accentColor)}
        >
          Sign in
        </button>
      </p>
    </>
  );
}

// ─── Auth content ─────────────────────────────────────────────────────────────

type Tab = 'login' | 'register';

export interface AuthScreenProps {
  /** Which tab to show initially. Defaults to 'login'. */
  initialTab?: Tab;
  /** Hex/CSS color for the left panel background, submit buttons, and accent links. */
  panelColor?: string;
  /** Hex/CSS value for the full page background. Defaults to white. */
  pageBackground?: string;
  /** Image URL for the left panel background. */
  panelImage?: string;
  /** Badge text shown at the top of the panel (e.g. "Est. 1993"). */
  badge?: string;
  /** Bullet-point feature list shown in the panel. */
  features?: string[];
  /** Pull quote shown in the panel. */
  quote?: string;
}

function AuthContent({
  initialTab = 'login',
  panelColor: panelColorProp,
  pageBackground: pageBackgroundProp,
  panelImage: panelImageProp,
  badge: badgeProp,
  features: featuresProp,
  quote: quoteProp,
}: AuthScreenProps) {
  const router = useRouter();
  const { buildPath } = useBasePath();
  const { config } = useTheme();
  const searchParams = useSearchParams();
  const redirectUrl = searchParams?.get('redirect') || buildPath('/');
  const { user, isLoading } = useAuth();
  const tabFromQuery = (searchParams?.get('tab') as Tab) ?? initialTab;
  const [tab, setTab] = useState<Tab>(tabFromQuery);
  const [direction, setDirection] = useState(0);

  const accentColor = panelColorProp ?? config.authPage?.panelColor;
  const pageBackground = pageBackgroundProp ?? config.authPage?.pageBackground;
  const panelImage = panelImageProp ?? config.authPage?.backgroundImage;
  const badge = badgeProp ?? config.authPage?.badge;
  const features = featuresProp ?? config.authPage?.features;
  const quote = quoteProp ?? config.authPage?.quote;

  const switchTab = (next: Tab) => {
    setDirection(next === 'register' ? 1 : -1);
    setTab(next);
  };

  if (!isLoading && user) {
    router.push(redirectUrl);
    return null;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" style={pageBackground ? { background: pageBackground } : undefined}>
      <div className="max-w-sm sm:max-w-6xl flex mx-auto w-full justify-center rounded-2xl overflow-hidden mt-24 sm:mt-12 mb-24">
        <LeftPanel accentColor={accentColor} panelImage={panelImage} badge={badge} features={features} quote={quote} />

        {/* Right panel */}
        <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white rounded-r-2xl overflow-y-auto">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="overflow-hidden mb-8">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={`header-${tab}`}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={slideTransition}
                >
                  <p
                    className="text-xs font-semibold tracking-[0.2em] text-hprimary/50 uppercase mb-2"
                    style={accentColor ? { color: `${accentColor}80` } : undefined}
                  >
                    {tab === 'login' ? 'Welcome back' : 'Get Started'}
                  </p>
                  <h1 className="text-3xl text-gray-900 mb-1">
                    {tab === 'login' ? 'Sign In' : 'Create Account'}
                  </h1>
                  <p className="text-sm text-gray-500">
                    {tab === 'login'
                      ? 'Enter your email to receive a sign-in code.'
                      : 'Join our community for personalized care.'}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Tabs */}
            <div className="relative flex gap-8 border-b border-gray-200 mb-8">
              {(['login', 'register'] as Tab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => switchTab(t)}
                  className={`relative pb-3 text-sm transition-colors ${
                    tab === t
                      ? 'font-semibold text-hprimary cursor-default'
                      : 'font-medium text-gray-400 hover:text-gray-600'
                  }`}
                  style={tab === t ? accentText(accentColor) : undefined}
                >
                  {t === 'login' ? 'Sign In' : 'Create Account'}
                  {tab === t && (
                    <motion.span
                      layoutId="tab-underline"
                      className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-hprimary"
                      style={accentBg(accentColor)}
                      transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Sliding form area */}
            <div className="overflow-hidden">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={tab}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={slideTransition}
                >
                  {tab === 'login' ? (
                    <LoginForm
                      onSwitchTab={() => switchTab('register')}
                      redirectUrl={redirectUrl}
                      accentColor={accentColor}
                    />
                  ) : (
                    <RegisterForm
                      onSwitchTab={() => switchTab('login')}
                      accentColor={accentColor}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AuthScreen(props: AuthScreenProps) {
  return (
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center">Loading…</div>}>
      <AuthContent {...props} />
    </Suspense>
  );
}
